// The catalogue (spec/14 §6, F-CAT-01, F-CAT-07): HQ's curated global list of games and apps.
//
// **Layout.** The page title and one line of purpose, then a horizontal Games | Apps switch — the page's
// primary view switch — directly under it. Each view opens with a header row: its title and one line on the
// left, its actions on the right, on the same row (Games: the IGDB search; Apps: "Add common apps" and
// "Add app"). Then a card grid. Games and apps share one card: the art (a 3:4 cover, or a square icon on a
// quiet well), the name in bold, a small badge, one quiet line, and an explicit action row. The whole card
// opens its main dialog — Media for a game, the edit stepper for an app — through its name, a button whose
// hit area is stretched over the card, so hover, focus and the cursor all say the card is one control.
//
//  - **Games.** A search → `searchCatalogue` → "From IGDB", a grid of cover cards, each with an **Add**
//    that mirrors the game (`mirrorCatalogueGame`) and flips to an "In catalogue ✓" badge. The mirror dedupes
//    on `igdbId`, so a repeat is safe. `422 not-a-base-game` is surfaced on the card; the provider being
//    unconfigured (`503 catalogue-provider-unavailable`) for the whole search. Below it, "In the catalogue":
//    the mirrored games, newest first, each card showing whether its hero and logo are set, with Launch
//    recipes and Refresh (re-read from IGDB and re-cache the cover, F-CAT-07) as explicit actions.
//  - **Apps.** Non-game titles HQ adds by hand — a browser, voice chat, a launcher — with a square icon
//    instead of a cover: "Add app" opens a short stepper in a dialog (`AppDialog.tsx`), "Add common apps" creates the
//    usual set where the catalogue lacks them (idempotent). A card opens its edit stepper; its "Icon" (or
//    "Pick icon") action opens Media, where the icon is uploaded or picked from SteamGridDB.
//
// **M3** (F-CAT-01, F-CAT-07, F-HQ-16): a game card's Edit opens `GameDialog.tsx` — every imported field editable,
// each change kept through refreshes, the `requiresElevation` flag, and archive (with how many venues offer it).
// "Add by hand" creates a game with no IGDB record (the provider down, or not knowing it); "Test detection"
// (`DetectionTestDialog.tsx`) runs a pasted process list against every title's rules. A third view, **Requests**
// (`RequestsInbox.tsx`), is the inbox of titles venues asked HQ to add (F-CAT-08).
//
// **Art.** A mirrored game's cover is served from **our own** media CDN (`coverUrl`), cached from IGDB on
// mirror; a game with no cached copy falls back to the IGDB reference, and one with no cover at all to a
// quiet placeholder (its initials and "No cover yet" — no copyrighted art in the repo). The **search
// preview** — nothing mirrored yet — always uses IGDB's image CDN. A game is a **game**, never a "title" in
// the UI — `title` is a different, deferred entity (the glossary).

import type { CatalogueKind, CatalogueSearchResult, Game } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, cx, EmptyState, SegmentedControl, Skeleton, useToast, VisuallyHidden } from '@clavian/ui-web';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Icon } from '../../shell/nav-icons';
import { LoadFailure } from '../../shell/LoadFailure';
import { SearchField } from '../../ui';
import { RequirePlatformPermission } from '../access';
import { HQ_CATALOG } from '../sections';
import { AppDialog } from './AppDialog';
import { DetectionDialog } from './DetectionDialog';
import { DetectionTestDialog } from './DetectionTestDialog';
import { GameDialog } from './GameDialog';
import { RequestsInbox } from './RequestsInbox';
import { LaunchRecipesDialog } from './LaunchRecipesDialog';
import { MediaDialog } from './MediaDialog';
import { catalogueCoverSrc, coverArtUrl, useCatalogueGames, useCatalogueSearch, useMirrorGame, useRefreshGame, useSeedCommonApps } from './queries';
import { productTone } from '../../org/product-art';
import { HqHeading } from '../heading';

export function CataloguePage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_CATALOG}>
      <Catalogue />
    </RequirePlatformPermission>
  );
}

/** The page's views: the two kinds of title, and the inbox of title requests. */
type CatalogueView = CatalogueKind | 'requests';

function Catalogue() {
  const { t } = useTranslation('hq');
  const [kind, setKind] = useState<CatalogueView>('game');
  // A request's "Find on IGDB" opens the Games view with its name searched (a new key remounts the search).
  const [seed, setSeed] = useState({ q: '', key: 0 });
  const findOnIgdb = (name: string) => {
    setSeed((current) => ({ q: name, key: current.key + 1 }));
    setKind('game');
  };
  return (
    <div className="console-page">
      <header className="console-catalogue-head">
        <HqHeading title={t('catalogue.title')} tip={t('catalogue.intro')} />
        <SegmentedControl<CatalogueView>
          className="console-catalogue-switch"
          label={t('catalogue.kindSwitch')}
          options={[
            { value: 'game', label: t('catalogue.kind.game'), icon: <Icon name="gamepad" /> },
            { value: 'app', label: t('catalogue.kind.app'), icon: <Icon name="window" /> },
            { value: 'requests', label: t('catalogue.kind.requests'), icon: <Icon name="layers" /> },
          ]}
          value={kind}
          onValueChange={setKind}
        />
      </header>
      {kind === 'game' ? <Games key={seed.key} initialQ={seed.q} /> : kind === 'app' ? <Apps /> : <RequestsInbox onFind={findOnIgdb} />}
    </div>
  );
}

/** A view's header row: its title and one line on the left, its actions on the right, on one row. */
function ViewBar({ id, title, intro, actions }: { readonly id: string; readonly title: ReactNode; readonly intro: ReactNode; readonly actions: ReactNode }) {
  return (
    <div className="console-catalogue-bar">
      <div className="console-catalogue-bar__text">
        {/* The view switcher above already says "Games" or "Apps": the heading stays for a screen reader, not twice on screen. */}
        <h2 id={id} className="clav-visually-hidden">
          {title}
        </h2>
        <p className="console-muted">{intro}</p>
      </div>
      <div className="console-catalogue-bar__actions">{actions}</div>
    </div>
  );
}

/** The card grid while it loads: the final layout, in skeleton. A status region, not the list it stands in for. */
function CardGridSkeleton({ square = false }: { readonly square?: boolean }) {
  const { t } = useTranslation('console');
  return (
    <div className={cx('console-catalogue-grid', square && 'console-catalogue-grid--apps')} role="status" aria-busy="true" aria-label={t('app.loading')}>
      {Array.from({ length: 8 }, (_, index) => (
        <div key={index} className="console-title-card console-title-card--skeleton" aria-hidden="true">
          <Skeleton className={cx('console-title-card__art', square && 'console-title-card__art--square')} />
          <div className="console-title-card__body">
            <Skeleton shape="text" width="70%" />
            <Skeleton shape="text" width="40%" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Apps: HQ-added non-game titles (spec/14 §6)
// ---------------------------------------------------------------------------------------------

function Apps() {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const apps = useCatalogueGames('app');
  const seed = useSeedCommonApps();
  const [adding, setAdding] = useState(false);

  const addCommon = () => {
    if (seed.isPending) return;
    seed.mutate(undefined, {
      onSuccess: (result) => show({ tone: 'ok', title: t('catalogue.apps.addCommonDone', { count: result.created }) }),
      onError: (err) => show({ tone: 'danger', title: problems.text(err, { fallback: t('catalogue.apps.addCommonFailed') }) }),
    });
  };

  let body: ReactNode;
  if (apps.data === undefined) {
    body = apps.isError ? (
      <LoadFailure error={apps.error} message={t('catalogue.apps.failed')} onRetry={() => void apps.refetch()} />
    ) : (
      <CardGridSkeleton square />
    );
  } else {
    const rows = apps.data.pages.flatMap((page) => page.data);
    body =
      rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="window" />}
          title={t('catalogue.apps.empty')}
          action={
            <Button variant="secondary" icon={<Icon name="layers" />} busy={seed.isPending} onClick={addCommon}>
              {t('catalogue.apps.addCommon')}
            </Button>
          }
        />
      ) : (
        <>
          <ul className="console-catalogue-grid console-catalogue-grid--apps" aria-label={t('catalogue.apps.caption')}>
            {rows.map((app) => (
              <AppCard key={app.id} app={app} />
            ))}
          </ul>
          {apps.hasNextPage ? (
            <Button className="console-hq-more" busy={apps.isFetchingNextPage} onClick={() => void apps.fetchNextPage()}>
              {t('catalogue.apps.more')}
            </Button>
          ) : null}
        </>
      );
  }

  return (
    <section className="console-catalogue-section" aria-labelledby="console-catalogue-apps-title">
      <ViewBar
        id="console-catalogue-apps-title"
        title={t('catalogue.apps.title')}
        intro={t('catalogue.apps.intro')}
        actions={
          <>
            <Button variant="secondary" icon={<Icon name="layers" />} busy={seed.isPending} label={t('catalogue.apps.addCommonLabel')} onClick={addCommon}>
              <span>{t('catalogue.apps.addCommon')}</span>
            </Button>
            <Button variant="primary" icon={<Icon name="plus" />} onClick={() => setAdding(true)}>
              {t('catalogue.apps.add')}
            </Button>
          </>
        }
      />
      {body}
      {adding ? <AppDialog open onOpenChange={setAdding} /> : null}
    </section>
  );
}

/** An app's card: the whole card edits it (name, category, launch, detection); "Icon" opens its media. */
function AppCard({ app }: { readonly app: Game }) {
  const { t } = useTranslation('hq');
  const [editing, setEditing] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const hasIcon = app.iconUrl !== null && app.iconUrl !== '';
  const names = app.detection.processNames;
  return (
    <TitleCard
      name={app.name}
      art={<Art name={app.name} src={app.iconUrl} square emptyHint={t('catalogue.apps.noIcon')} />}
      openLabel={t('catalogue.apps.editLabel', { app: app.name })}
      openHint={t('catalogue.card.openEdit')}
      onOpen={() => setEditing(true)}
      tags={
        <>
          <Badge tone="neutral">{t(`catalogue.apps.category.${app.suggestedCategory ?? 'apps'}`)}</Badge>
          {/* F-CAT-09: stations can install it into their player account when a venue asks. */}
          {app.provisioning !== null ? <Badge tone="info">{t('catalogue.apps.installsForPlayers')}</Badge> : null}
        </>
      }
      detail={
        <span className="console-title-card__detail" title={names.length > 1 ? names.join(', ') : undefined}>
          {t('catalogue.apps.detectionCount', { count: names.length, names: names.join(', ') })}
        </span>
      }
      actions={
        <>
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="image" />}
            label={hasIcon ? t('catalogue.apps.iconLabel', { app: app.name }) : t('catalogue.apps.pickIconLabel', { app: app.name })}
            onClick={() => setMediaOpen(true)}
          >
            <span>{hasIcon ? t('catalogue.apps.icon') : t('catalogue.apps.pickIcon')}</span>
          </Button>
          {editing ? <AppDialog app={app} open onOpenChange={setEditing} /> : null}
          {mediaOpen ? <MediaDialog game={app} open onOpenChange={setMediaOpen} /> : null}
        </>
      }
    />
  );
}

// ---------------------------------------------------------------------------------------------
// Games: the search, its results, and the mirrored games
// ---------------------------------------------------------------------------------------------

function Games({ initialQ = '' }: { readonly initialQ?: string }) {
  const { t } = useTranslation('hq');
  const errorId = useId();
  const [q, setQ] = useState(initialQ);
  const [text, setText] = useState(initialQ);
  const [error, setError] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState(false);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    const wanted = text.trim();
    // The api takes 1–120 characters after trimming; refuse the rest here rather than round-trip it.
    const refused = wanted.length < 1 ? t('catalogue.add.tooShort') : wanted.length > 120 ? t('catalogue.add.tooLong', { count: 120 }) : undefined;
    setError(refused);
    if (refused === undefined) setQ(wanted);
  };

  return (
    <>
      <section className="console-catalogue-section" aria-labelledby="console-catalogue-games-title">
        <ViewBar
          id="console-catalogue-games-title"
          title={t('catalogue.games.title')}
          intro={t('catalogue.games.intro')}
          actions={
            <form className="console-catalogue-search" role="search" aria-label={t('catalogue.add.label')} noValidate onSubmit={apply}>
              <SearchField
                label={t('catalogue.add.label')}
                placeholder={t('catalogue.add.placeholder')}
                value={text}
                aria-invalid={error === undefined ? undefined : true}
                aria-describedby={error === undefined ? undefined : errorId}
                onChange={(event) => setText(event.target.value)}
              />
              <Button type="submit" variant="primary" icon={<Icon name="search" />}>
                {t('catalogue.add.submit')}
              </Button>
              {error === undefined ? null : (
                <p id={errorId} className="console-catalogue-search__error" role="alert">
                  {error}
                </p>
              )}
            </form>
          }
        />
        <div className="console-catalogue-tools">
          <Button variant="secondary" size="sm" icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
            {t('catalogue.game.createAction')}
          </Button>
          <Button variant="secondary" size="sm" icon={<Icon name="gamepad" />} onClick={() => setTesting(true)}>
            {t('catalogue.detectionTest.action')}
          </Button>
        </div>
        {q === '' ? null : <SearchResults q={q} onClear={() => { setQ(''); setText(''); }} />}
        {creating ? <GameDialog open onOpenChange={setCreating} /> : null}
        {testing ? <DetectionTestDialog open onOpenChange={setTesting} /> : null}
      </section>
      <BrowseCatalogue />
    </>
  );
}

function SearchResults({ q, onClear }: { readonly q: string; readonly onClear: () => void }) {
  const { t } = useTranslation('hq');
  const search = useCatalogueSearch(q);

  let body: ReactNode;
  if (search.data === undefined) {
    // The provider being unconfigured is not a load failure to retry — it is a deployment state.
    if (problemCode(search.error) === PROBLEM.catalogueProviderUnavailable) {
      body = (
        <p className="console-alert" role="alert">
          {t('catalogue.add.providerUnavailable')}
        </p>
      );
    } else {
      body = search.isError ? (
        <LoadFailure error={search.error} message={t('catalogue.add.failed')} onRetry={() => void search.refetch()} />
      ) : (
        <CardGridSkeleton />
      );
    }
  } else {
    const results = search.data.data;
    body = (
      <>
        <p className="console-muted" role="status">
          {t('catalogue.add.found', { count: results.length, q })}
        </p>
        {results.length === 0 ? null : (
          <ul className="console-catalogue-grid" aria-label={t('catalogue.add.results')}>
            {results.map((result) => (
              <SearchResultCard key={result.igdbId} result={result} />
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <div className="console-catalogue-results">
      <div className="console-catalogue-subhead">
        <h3>{t('catalogue.add.resultsTitle')}</h3>
        <Button variant="ghost" size="sm" icon={<Icon name="close" />} onClick={onClear}>
          {t('catalogue.add.clear')}
        </Button>
      </div>
      {body}
    </div>
  );
}

/**
 * One base-game candidate: its cover, name and year, and either the "In catalogue ✓" badge or an Add
 * button. The button mirrors the game and flips to the badge on success; the mirror dedupes on
 * `igdbId`, so pressing it twice is safe. A `422 not-a-base-game` is surfaced on the card.
 */
function SearchResultCard({ result }: { readonly result: CatalogueSearchResult }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const mirror = useMirrorGame();
  const [added, setAdded] = useState(result.alreadyInCatalogue);

  const actions = added ? (
    <p className="console-catalogue-card__held">
      <Badge tone="ok">
        {t('catalogue.add.inCatalogue')} <span aria-hidden="true">✓</span>
      </Badge>
      <VisuallyHidden>{t('catalogue.add.inCatalogueFor', { game: result.name })}</VisuallyHidden>
    </p>
  ) : (
    <Button
      variant="secondary"
      size="sm"
      icon={<Icon name="plus" />}
      busy={mirror.isPending}
      label={t('catalogue.add.buttonLabel', { game: result.name })}
      onClick={() => {
        if (!mirror.isPending) mirror.mutate(result.igdbId, { onSuccess: () => setAdded(true) });
      }}
    >
      {t('catalogue.add.button')}
    </Button>
  );

  // The search preview is never mirrored yet, so it always draws IGDB's image CDN (no `coverUrl`).
  const coverSrc = result.coverImageId !== null && result.coverImageId !== '' ? coverArtUrl(result.coverImageId) : null;
  return (
    <TitleCard
      name={result.name}
      art={<Art name={result.name} src={coverSrc} emptyHint={t('catalogue.card.noCover')} />}
      detail={<YearAndFranchise year={result.firstReleaseYear} franchise={null} />}
      actions={actions}
      error={
        mirror.isError ? (
          <p className="console-catalogue-card__error" role="alert">
            {addFailureKey(mirror.error) === 'catalogue.add.addFailed'
              ? problems.text(mirror.error, { fallback: t('catalogue.add.addFailed', { game: result.name }) })
              : t(addFailureKey(mirror.error), { game: result.name })}
          </p>
        ) : null
      }
    />
  );
}

/** The copy key for a failed mirror, by the problem's code (spec/14 §6). Each takes a `{{game}}`. */
function addFailureKey(error: unknown): string {
  const code = problemCode(error);
  if (code === PROBLEM.notABaseGame) return 'catalogue.add.notBaseGame';
  if (code === PROBLEM.catalogueProviderUnavailable) return 'catalogue.add.addProviderUnavailable';
  if (code === PROBLEM.forbidden) return 'catalogue.add.addForbidden';
  return 'catalogue.add.addFailed';
}

function BrowseCatalogue() {
  const { t } = useTranslation('hq');
  const games = useCatalogueGames('game');

  let body: ReactNode;
  let count: number | null = null;
  if (games.data === undefined) {
    body = games.isError ? (
      <LoadFailure error={games.error} message={t('catalogue.browse.failed')} onRetry={() => void games.refetch()} />
    ) : (
      <CardGridSkeleton />
    );
  } else {
    const rows = games.data.pages.flatMap((page) => page.data);
    count = games.hasNextPage ? null : rows.length;
    body =
      rows.length === 0 ? (
        <EmptyState icon={<Icon name="gamepad" />} title={t('catalogue.browse.empty')} />
      ) : (
        <>
          <ul className="console-catalogue-grid" aria-label={t('catalogue.browse.caption')}>
            {rows.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </ul>
          {games.hasNextPage ? (
            <Button className="console-hq-more" busy={games.isFetchingNextPage} onClick={() => void games.fetchNextPage()}>
              {t('catalogue.browse.more')}
            </Button>
          ) : null}
          {games.isFetchNextPageError ? <LoadFailure error={games.error} onRetry={() => void games.fetchNextPage()} /> : null}
        </>
      );
  }

  return (
    <section className="console-catalogue-section" aria-labelledby="console-catalogue-browse-title">
      <div className="console-catalogue-subhead">
        <h3 id="console-catalogue-browse-title">{t('catalogue.browse.title')}</h3>
        {count === null || count === 0 ? null : <span className="console-catalogue-subhead__count">{t('catalogue.browse.count', { count })}</span>}
      </div>
      {body}
    </section>
  );
}

/**
 * A mirrored game's card: the whole card opens its media (hero, logo, trailers — ADR-0028); Launch recipes
 * (F-CAT-05), Detection (F-CAT-04) and Refresh from IGDB (F-CAT-07) are explicit actions. Its hero and logo status read at a glance.
 */
function GameCard({ game }: { readonly game: Game }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const refresh = useRefreshGame();
  const [editOpen, setEditOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);
  const [detectionOpen, setDetectionOpen] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const recipes = game.launchRecipes.length;
  const hasHero = game.heroUrl !== null;
  const hasLogo = game.logoUrl !== null;

  return (
    <TitleCard
      name={game.name}
      art={<Art name={game.name} src={catalogueCoverSrc(game)} emptyHint={t('catalogue.card.noCover')} />}
      openLabel={t('catalogue.media.actionLabel', { game: game.name })}
      openHint={t('catalogue.card.openMedia')}
      openIcon="image"
      onOpen={() => setMediaOpen(true)}
      tags={
        <>
          {game.archivedAt !== null ? <Badge tone="warn">{t('catalogue.card.archived')}</Badge> : null}
          {game.requiresElevation ? <Badge tone="info">{t('catalogue.card.admin')}</Badge> : null}
          {game.igdbId === null ? <Badge tone="neutral">{t('catalogue.card.byHand')}</Badge> : null}
          {game.ageRating === null ? null : <Badge tone="neutral">{game.ageRating}</Badge>}
          <Badge tone={hasHero ? 'ok' : 'neutral'}>{hasHero ? t('catalogue.card.hero') : t('catalogue.card.noHero')}</Badge>
          <Badge tone={hasLogo ? 'ok' : 'neutral'}>{hasLogo ? t('catalogue.card.logo') : t('catalogue.card.noLogo')}</Badge>
        </>
      }
      detail={<YearAndFranchise year={game.firstReleaseYear} franchise={game.franchise} />}
      actions={
        <>
          <Button variant="secondary" size="sm" icon={<Icon name="pencil" />} label={t('catalogue.game.editLabel', { game: game.name })} onClick={() => setEditOpen(true)}>
            <span>{t('catalogue.game.editAction')}</span>
          </Button>
          <Button variant="secondary" size="sm" icon={<Icon name="launch" />} label={t('catalogue.launch.actionLabel', { game: game.name })} onClick={() => setLaunchOpen(true)}>
            <span>{recipes === 0 ? t('catalogue.launch.action') : t('catalogue.launch.actionCount', { count: recipes })}</span>
          </Button>
          <Button variant="secondary" size="sm" icon={<Icon name="gamepad" />} label={t('catalogue.detection.actionLabel', { game: game.name })} onClick={() => setDetectionOpen(true)}>
            <span>{t('catalogue.detection.action')}</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon name="refresh" />}
            busy={refresh.isPending}
            disabled={game.igdbId === null}
            label={t('catalogue.browse.refreshLabel', { game: game.name })}
            onClick={() => {
              if (!refresh.isPending) refresh.mutate(game.id);
            }}
          />
          {editOpen ? <GameDialog game={game} open onOpenChange={setEditOpen} /> : null}
          {launchOpen ? <LaunchRecipesDialog game={game} open onOpenChange={setLaunchOpen} /> : null}
          {detectionOpen ? <DetectionDialog game={game} open onOpenChange={setDetectionOpen} /> : null}
          {mediaOpen ? <MediaDialog game={game} open onOpenChange={setMediaOpen} /> : null}
        </>
      }
      error={
        refresh.isError ? (
          <p className="console-catalogue-card__error" role="alert">
            {problems.text(refresh.error, { fallback: t('catalogue.browse.refreshFailed', { game: game.name }) })}
          </p>
        ) : null
      }
    />
  );
}

function YearAndFranchise({ year, franchise }: { readonly year: number | null; readonly franchise: string | null }) {
  const { t } = useTranslation('hq');
  return (
    <>
      <span className="console-title-card__detail">{year === null ? t('catalogue.card.yearUnknown') : t('catalogue.card.year', { year })}</span>
      {franchise === null || franchise === '' ? null : (
        <span className="console-title-card__detail" title={franchise}>
          {t('catalogue.card.franchise', { franchise })}
        </span>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// The shared title card
// ---------------------------------------------------------------------------------------------

interface TitleCardProps {
  readonly name: string;
  readonly art: ReactNode;
  /** The card's main action — its name becomes a button whose hit area covers the card. */
  readonly onOpen?: () => void;
  /** That button's accessible name ("Edit Discord", "Media for Half-Life 2"); it contains the visible name. */
  readonly openLabel?: string;
  /** What opening does, shown over the art on hover and focus ("Edit", "Media"), and its glyph. */
  readonly openHint?: string;
  readonly openIcon?: string;
  readonly tags?: ReactNode;
  readonly detail?: ReactNode;
  readonly actions?: ReactNode;
  readonly error?: ReactNode;
}

function TitleCard({ name, art, onOpen, openLabel, openHint, openIcon = 'pencil', tags, detail, actions, error }: TitleCardProps) {
  return (
    <li className={cx('console-title-card', onOpen !== undefined && 'console-title-card--open')}>
      <span className="console-title-card__media">
        {art}
        {onOpen !== undefined && openHint !== undefined ? (
          <span className="console-title-card__hint" aria-hidden="true">
            <Icon name={openIcon} />
            {openHint}
          </span>
        ) : null}
      </span>
      <div className="console-title-card__body">
        {onOpen === undefined ? (
          <span className="console-title-card__name">{name}</span>
        ) : (
          <button type="button" className="console-title-card__name console-title-card__open" aria-label={openLabel} onClick={onOpen}>
            {name}
          </button>
        )}
        {tags === undefined ? null : <span className="console-title-card__tags">{tags}</span>}
        {detail ?? null}
      </div>
      {actions === undefined ? null : <div className="console-title-card__actions">{actions}</div>}
      {error ?? null}
    </li>
  );
}

/**
 * A title's art: a game's cover at IGDB's portrait shape (264 × 374, drawn 3:4) filling its frame, or an
 * app's square icon contained on a quiet well with padding (icons are transparent PNG/WebP). With no art —
 * or art that fails to load — a quiet placeholder: small initials and a hint ("No icon yet"). The real image
 * carries the title's name as its alt; the placeholder is decorative beside the visible name.
 */
function Art({ name, src, square = false, emptyHint }: { readonly name: string; readonly src: string | null; readonly square?: boolean; readonly emptyHint: string }) {
  const { t } = useTranslation('hq');
  const [failed, setFailed] = useState<string | null>(null);
  const frame = cx('console-title-card__art', square && 'console-title-card__art--square');
  if (src !== null && src !== '' && failed !== src) {
    return (
      <span className={frame}>
        <img
          src={src}
          alt={square ? t('catalogue.apps.iconAlt', { app: name }) : t('catalogue.card.coverAlt', { game: name })}
          loading="lazy"
          width={264}
          height={square ? 264 : 374}
          onError={() => setFailed(src)}
        />
      </span>
    );
  }
  // The plate a dish or a library title wears without a picture (ADR-0043): its own tint and heavy initials, and the hint
  // that the catalogue still needs its art.
  return (
    <span className={cx(frame, 'console-title-card__art--empty', 'console-product-art')} data-tone={productTone(name)} aria-hidden="true">
      <span className="console-title-card__initials console-product-art__initial">{initials(name)}</span>
      <span className="console-title-card__empty">{emptyHint}</span>
    </span>
  );
}

/** Up to two initials of a title's name, for its placeholder. */
function initials(name: string): string {
  return name
    .split(/[\s:]+/)
    .filter((word) => /^[A-Za-z0-9]/.test(word))
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}
